import React, { useState, useEffect } from 'react';
import { 
  User, Crown, CreditCard, Calendar, Clock, LogOut, 
  CheckCircle, AlertCircle, Loader2, X, History,
  BookOpen, Target, TrendingUp, Award, BarChart3
} from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const UserProfile = ({ isOpen, onClose, user, onLogout }) => {
  const [subscription, setSubscription] = useState(null);
  const [paymentHistory, setPaymentHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [performance, setPerformance] = useState(null);
  const [activeTab, setActiveTab] = useState('progress');

  useEffect(() => {
    if (isOpen && user) {
      fetchUserData();
    }
  }, [isOpen, user]);

  const fetchUserData = async () => {
    setLoading(true);
    try {
      const userId = user?.user_id || user?.id;
      
      if (userId) {
        const [subRes, perfRes] = await Promise.allSettled([
          axios.get(`${API}/stripe/subscription/user/${userId}`),
          axios.get(`${API}/adaptive/performance/${userId}`)
        ]);
        
        if (subRes.status === 'fulfilled' && subRes.value.data.has_subscription) {
          setSubscription(subRes.value.data.subscription);
        }
        if (perfRes.status === 'fulfilled') {
          setPerformance(perfRes.value.data);
        }
      }
      
      const email = user?.email;
      if (email) {
        try {
          const historyResponse = await axios.get(`${API}/user/payment-history/${encodeURIComponent(email)}`);
          setPaymentHistory(historyResponse.data.payments || []);
        } catch (e) {}
      }
    } catch (err) {
      console.error('Error fetching user data:', err);
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('he-IL', { year: 'numeric', month: 'long', day: 'numeric' });
  };

  const getDaysRemaining = (expiresAt) => {
    if (!expiresAt) return null;
    const diff = Math.ceil((new Date(expiresAt) - new Date()) / (1000 * 60 * 60 * 24));
    return diff > 0 ? diff : 0;
  };

  const getPlanBadge = (planId) => {
    const map = {
      trial: { text: 'ניסיון חינם', color: 'bg-teal-100 text-teal-700' },
      monthly: { text: 'מנוי חודשי', color: 'bg-purple-100 text-purple-700' },
      yearly: { text: 'מנוי שנתי', color: 'bg-amber-100 text-amber-700' },
    };
    return map[planId] || { text: 'ללא מנוי', color: 'bg-slate-100 text-slate-700' };
  };

  const accuracy = performance?.overall_accuracy || 0;
  const totalAnswered = performance?.total_questions_answered || 0;
  const totalCorrect = performance?.total_correct || 0;
  const streak = performance?.current_streak || 0;
  const maxStreak = performance?.max_streak || 0;

  const getAccuracyColor = (acc) => {
    if (acc >= 80) return 'text-green-500';
    if (acc >= 60) return 'text-amber-500';
    return 'text-red-500';
  };

  const getAccuracyBg = (acc) => {
    if (acc >= 80) return 'from-green-500 to-emerald-600';
    if (acc >= 60) return 'from-amber-500 to-orange-600';
    return 'from-red-500 to-rose-600';
  };

  const tabs = [
    { id: 'progress', label: 'התקדמות', icon: TrendingUp },
    { id: 'subscription', label: 'מנוי', icon: Crown },
    { id: 'payments', label: 'תשלומים', icon: History },
  ];

  return (
    <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" dir="rtl">
      <div className="bg-white rounded-2xl max-w-lg w-full relative max-h-[90vh] overflow-y-auto shadow-2xl">
        {/* Close */}
        <button onClick={onClose} className="absolute top-3 left-3 p-2 hover:bg-slate-100 rounded-full transition-colors z-10" data-testid="profile-close-button">
          <X className="w-5 h-5 text-slate-500" />
        </button>

        {/* Header */}
        <div className="bg-gradient-to-br from-slate-800 to-slate-900 text-white p-6 rounded-t-2xl text-center">
          <div className="w-16 h-16 rounded-full bg-gradient-to-br from-teal-400 to-teal-600 mx-auto flex items-center justify-center mb-3 shadow-lg ring-4 ring-white/20">
            {user?.picture ? (
              <img src={user.picture} alt={user.name} className="w-full h-full rounded-full object-cover" />
            ) : (
              <User className="w-8 h-8 text-white" />
            )}
          </div>
          <h2 className="text-xl font-bold" data-testid="profile-name">{user?.name || 'משתמש'}</h2>
          <p className="text-slate-400 text-sm">{user?.email}</p>
          
          {/* Quick stats row */}
          {totalAnswered > 0 && (
            <div className="flex justify-center gap-6 mt-4 pt-4 border-t border-white/10">
              <div className="text-center">
                <div className={`text-lg font-bold ${getAccuracyColor(accuracy)}`}>{accuracy}%</div>
                <div className="text-xs text-slate-400">דיוק</div>
              </div>
              <div className="text-center">
                <div className="text-lg font-bold text-teal-400">{totalAnswered}</div>
                <div className="text-xs text-slate-400">שאלות</div>
              </div>
              <div className="text-center">
                <div className="text-lg font-bold text-amber-400">{streak}</div>
                <div className="text-xs text-slate-400">רצף</div>
              </div>
            </div>
          )}
        </div>

        {/* Tabs */}
        <div className="flex border-b border-slate-200">
          {tabs.map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex-1 py-3 text-sm font-medium flex items-center justify-center gap-1.5 transition-colors ${
                activeTab === tab.id 
                  ? 'text-teal-600 border-b-2 border-teal-500' 
                  : 'text-slate-500 hover:text-slate-700'
              }`}
              data-testid={`profile-tab-${tab.id}`}
            >
              <tab.icon className="w-4 h-4" />
              {tab.label}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 text-teal-500 animate-spin" />
          </div>
        ) : (
          <div className="p-5">
            {/* Progress Tab */}
            {activeTab === 'progress' && (
              <div className="space-y-4" data-testid="progress-tab">
                {totalAnswered > 0 ? (
                  <>
                    {/* Accuracy Circle */}
                    <div className="bg-slate-50 rounded-xl p-5 text-center">
                      <div className={`inline-flex items-center justify-center w-24 h-24 rounded-full bg-gradient-to-br ${getAccuracyBg(accuracy)} text-white mb-3`}>
                        <span className="text-2xl font-black">{accuracy}%</span>
                      </div>
                      <p className="text-sm text-slate-600 font-medium">דיוק כללי</p>
                    </div>

                    {/* Stats Grid */}
                    <div className="grid grid-cols-2 gap-3">
                      <div className="bg-white border border-slate-200 rounded-xl p-4 text-center">
                        <BookOpen className="w-5 h-5 text-teal-500 mx-auto mb-1" />
                        <div className="text-xl font-bold text-slate-800">{totalAnswered}</div>
                        <div className="text-xs text-slate-500">שאלות נענו</div>
                      </div>
                      <div className="bg-white border border-slate-200 rounded-xl p-4 text-center">
                        <Target className="w-5 h-5 text-green-500 mx-auto mb-1" />
                        <div className="text-xl font-bold text-slate-800">{totalCorrect}</div>
                        <div className="text-xs text-slate-500">תשובות נכונות</div>
                      </div>
                      <div className="bg-white border border-slate-200 rounded-xl p-4 text-center">
                        <TrendingUp className="w-5 h-5 text-amber-500 mx-auto mb-1" />
                        <div className="text-xl font-bold text-slate-800">{streak}</div>
                        <div className="text-xs text-slate-500">רצף נוכחי</div>
                      </div>
                      <div className="bg-white border border-slate-200 rounded-xl p-4 text-center">
                        <Award className="w-5 h-5 text-purple-500 mx-auto mb-1" />
                        <div className="text-xl font-bold text-slate-800">{maxStreak}</div>
                        <div className="text-xs text-slate-500">רצף שיא</div>
                      </div>
                    </div>

                    {/* Subjects breakdown */}
                    {performance?.subjects && Object.keys(performance.subjects).length > 0 && (
                      <div className="bg-white border border-slate-200 rounded-xl p-4">
                        <h3 className="text-sm font-bold text-slate-700 mb-3 flex items-center gap-2">
                          <BarChart3 className="w-4 h-4" />
                          התקדמות לפי נושא
                        </h3>
                        <div className="space-y-2">
                          {Object.entries(performance.subjects).slice(0, 6).map(([name, data]) => (
                            <div key={name} className="flex items-center gap-2">
                              <span className="text-xs text-slate-600 w-24 truncate text-right">{name}</span>
                              <div className="flex-1 h-2 bg-slate-100 rounded-full overflow-hidden">
                                <div
                                  className={`h-full rounded-full ${data.accuracy >= 80 ? 'bg-green-500' : data.accuracy >= 60 ? 'bg-amber-500' : 'bg-red-500'}`}
                                  style={{ width: `${data.accuracy || 0}%` }}
                                />
                              </div>
                              <span className="text-xs font-bold text-slate-700 w-10 text-left">{data.accuracy || 0}%</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </>
                ) : (
                  <div className="text-center py-8">
                    <Target className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                    <p className="text-slate-500 font-medium">עדיין אין נתוני התקדמות</p>
                    <p className="text-sm text-slate-400 mt-1">התחל לתרגל כדי לראות את ההתקדמות שלך</p>
                  </div>
                )}
              </div>
            )}

            {/* Subscription Tab */}
            {activeTab === 'subscription' && (
              <div data-testid="subscription-tab">
                {subscription ? (
                  <div className="space-y-3">
                    <div className="flex items-center justify-between mb-4">
                      <span className={`px-3 py-1 rounded-full text-sm font-bold ${getPlanBadge(subscription.plan_id).color}`}>
                        {getPlanBadge(subscription.plan_id).text}
                      </span>
                      <Crown className="w-5 h-5 text-amber-500" />
                    </div>
                    <div className="flex items-center justify-between py-2.5 border-b border-slate-100">
                      <span className="font-medium text-slate-900">{formatDate(subscription.started_at)}</span>
                      <span className="text-sm text-slate-600 flex items-center gap-1.5"><Calendar className="w-4 h-4" /> התחלה</span>
                    </div>
                    <div className="flex items-center justify-between py-2.5 border-b border-slate-100">
                      <span className="font-medium text-slate-900">{formatDate(subscription.expires_at)}</span>
                      <span className="text-sm text-slate-600 flex items-center gap-1.5"><Clock className="w-4 h-4" /> תוקף</span>
                    </div>
                    {subscription.expires_at && (
                      <div className="flex items-center justify-between py-2.5">
                        <span className={`font-bold text-lg ${getDaysRemaining(subscription.expires_at) <= 7 ? 'text-red-500' : 'text-teal-600'}`}>
                          {getDaysRemaining(subscription.expires_at)} ימים
                        </span>
                        <span className="text-sm text-slate-600">נותרו</span>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <AlertCircle className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                    <p className="text-slate-500 font-medium">אין מנוי פעיל</p>
                    <button onClick={onClose} className="mt-4 bg-teal-500 text-white px-6 py-2.5 rounded-xl font-bold hover:bg-teal-600 transition-colors text-sm">
                      הצטרף עכשיו
                    </button>
                  </div>
                )}
              </div>
            )}

            {/* Payments Tab */}
            {activeTab === 'payments' && (
              <div data-testid="payments-tab">
                {paymentHistory.length > 0 ? (
                  <div className="space-y-2">
                    {paymentHistory.slice(0, 5).map((payment, index) => (
                      <div key={payment.id || index} className="flex items-center justify-between py-3 border-b border-slate-100 last:border-0">
                        <div className="flex items-center gap-2">
                          {payment.payment_status === 'paid' ? (
                            <CheckCircle className="w-4 h-4 text-green-500" />
                          ) : (
                            <AlertCircle className="w-4 h-4 text-amber-500" />
                          )}
                          <div>
                            <p className="font-medium text-sm text-slate-900">{payment.plan_name || 'תשלום'}</p>
                            <p className="text-xs text-slate-500">{formatDate(payment.created_at)}</p>
                          </div>
                        </div>
                        <span className="font-bold text-slate-900">₪{payment.amount}</span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <History className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                    <p className="text-slate-500 font-medium">אין היסטוריית תשלומים</p>
                  </div>
                )}
              </div>
            )}

            {/* Logout */}
            <div className="mt-5 pt-4 border-t border-slate-200">
              <button
                onClick={onLogout}
                className="w-full flex items-center justify-center gap-2 py-2.5 text-red-500 hover:bg-red-50 rounded-xl transition-colors font-medium text-sm"
                data-testid="profile-logout-button"
              >
                <LogOut className="w-4 h-4" />
                התנתק
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default UserProfile;
